import { useEffect, useState } from "react";
import { API, errMsg } from "../../lib/api";
import { StatCard } from "../../components/bits";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    API.get("/admin/overview").then(({ data }) => setData(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return <p className="py-16 text-center text-sm text-muted-foreground" data-testid="admin-overview-loading">Loading overview…</p>;

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-2xl font-bold mb-6" data-testid="admin-overview-title">Platform overview</h1>
      <h2 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Users &amp; revenue</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total users" value={data.users.total} testid="admin-stat-users" />
        <StatCard label="Active subscribers" value={data.users.active_subscribers} testid="admin-stat-subscribers" />
        <StatCard label="Total revenue" value={`£${data.revenue.total_gbp.toFixed(2)}`} testid="admin-stat-revenue" />
        <StatCard label="MRR (est.)" value={`£${data.revenue.mrr_gbp.toFixed(2)}`} sub={`${data.revenue.failed_payments} failed payments`} testid="admin-stat-mrr" />
      </div>
      {Object.keys(data.revenue.plan_breakdown).length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5 mb-8" data-testid="plan-breakdown">
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Active plans</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(data.revenue.plan_breakdown).map(([k, v]) => (
              <span key={k} className="text-sm"><span className="font-semibold">{k}:</span> {v}</span>
            ))}
          </div>
        </div>
      )}
      <h2 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Question bank</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Published" value={data.questions.by_status.published || 0} testid="admin-stat-published" />
        <StatCard label="Drafts" value={data.questions.by_status.draft || 0} testid="admin-stat-drafts" />
        <StatCard label="Awaiting review" value={(data.questions.by_status.ai_review || 0) + (data.questions.by_status.clinical_review || 0) + (data.questions.by_status.approved || 0)} testid="admin-stat-review" />
        <StatCard label="Open reports" value={data.reports_open} sub={`${data.flags_total} user flags`} testid="admin-stat-reports" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-5" data-testid="admin-topic-counts">
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Published per topic</h3>
          <div className="divide-y divide-border">
            {Object.entries(data.questions.published_by_topic).map(([t, n]) => (
              <div key={t} className="py-2 flex justify-between text-sm">
                <span className="capitalize">{t.replace(/-/g, " ")}</span><span className="font-semibold">{n}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-5" data-testid="admin-difficulty-counts">
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Published per difficulty</h3>
          <div className="divide-y divide-border">
            {Object.entries(data.questions.published_by_difficulty).map(([d, n]) => (
              <div key={d} className="py-2 flex justify-between text-sm">
                <span className="capitalize">{d}</span><span className="font-semibold">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
